import React, { useCallback, memo, useMemo, useState, useEffect } from 'react';
import { identity } from 'ramda';
import { useFocus } from 'js/hooks/useFocus';
import { clsb, clsx } from 'js/utils/classNames.util';
import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { areOmitedPropsEqual } from 'js/utils/methods.util';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import * as Types from './types';
import { AccordionCellNew } from './components/AccordionCellNew';

import style from './AccordionRowNew.scss';

export const arePropsEqual = (prevProps: Types.IAccordionRowProps, nextProps: Types.IAccordionRowProps): boolean => {
    const propsToOmit = ['bodyCells', 'headerCells'];

    return areOmitedPropsEqual(prevProps, nextProps, propsToOmit)
        && prevProps.bodyCells?.length === nextProps.bodyCells?.length
        && prevProps.headerCells?.length === nextProps.headerCells?.length;
};

/**
 * @example
 * // when isIndependentMode = false, only one accordion row can be expanded at time
 * const [expandedItemId, setExpandedItemId] = useState(someId);
 * const expandItem = useCallback((id) => setExpandedItemId(id), []);
 * <AccordionRow id={id} expandItem={expandItem} isExpanded={expandedItemId === id} />
 */

export const AccordionRowNew: React.FC<Types.IAccordionRowProps> = memo((props) => {
    const {
        id,
        tableRowId,
        bodyCells = [],
        headerCells = [],
        onAccordionExpand,
        onAccordionCollapse,
        classNames: propsClassNames,
        isExpandedByDefault = false,
        isIndependentMode = true,
        expandItem = identity,
        isExpanded = false,
        isFocused,
        unmountOnExit = false,
        isDisabled = false
    } = props;
    const translate = useTranslations();
    const isMounted = useIsMounted();
    const [rowRef, setFocus] = useFocus();
    const classNames = useMemo(() => propsClassNames || {}, [propsClassNames]);
    const [isRowExpanded, setIsRowExpanded] = useState(isExpandedByDefault);

    const toggleExpandedHandler = useCallback(
        (isExpandedState: boolean) => isExpandedState ? onAccordionExpand?.(id) : onAccordionCollapse?.(id),
        [onAccordionExpand, onAccordionCollapse, id]);

    const toggleExpanded = useCallback((event: React.MouseEvent<HTMLElement>) => {
        const shouldToggleExpanded = event.target instanceof HTMLElement &&
            (event.target.closest(`.${style.accordionHeaderContainer}`)
            || event.target.classList.contains(style.accordionContainer));

        if (!isMounted() || !shouldToggleExpanded) {
            return;
        }

        event.preventDefault();

        if (isIndependentMode) {
            setIsRowExpanded((prev) => {
                toggleExpandedHandler(!prev);

                return !prev;
            });
        } else {
            expandItem(isExpanded ? null : id);
            toggleExpandedHandler(!isExpanded);
        }
    }, [expandItem, isExpanded, isIndependentMode, id, isMounted, toggleExpandedHandler]);

    useEffect(() => {
        if (isFocused) {
            setFocus();
        }
    });

    const isActuallyExpanded = isExpanded || isRowExpanded;

    return (
        <AccessibilityElement
            elementRef={rowRef}
            {...!isDisabled ? { onClick: toggleExpanded } : { tabIndex: -1 }}
            className={clsx([
                clsb([style, classNames], ['accordionContainer', isActuallyExpanded && 'activeContainer']),
                tableRowId ? `cms-accordion-row-${tableRowId}` : '',
                isDisabled ? clsb([style, classNames], ['accordionContainerDisabled']) : '',
                isDisabled ? 'cms-accordion-row-disabled' : '',
                'cms-accordion-row'
            ])}
            data-table-row-id={tableRowId}
            ariaLiveLabel={isActuallyExpanded
                ? translate('playtech.mobile.system.table.aria-label.collapse')
                : translate('playtech.mobile.system.table.aria-label.expand')
            }
        >
            <div
                className={`${clsb([style, classNames], ['accordionHeaderContainer'])} ${tableRowId ? `cms-accordion-head-${tableRowId}` : ''} cms-accordion-head`}
            >
                {headerCells.map(cell => (
                    <AccordionCellNew
                        key={cell.id}
                        content={cell.content}
                        className={`${clsb([style, classNames], ['accordionHeaderCell'])} ${cell.className || ''}`}
                    />
                ))}
                <div className={clsb([style, classNames], ['accordionHeaderArrowRow'])}>
                    <Icon type="arrowDownIcon" className={`${style.accordionIcon} ${isActuallyExpanded ? style.expanded : ''}`} />
                </div>
            </div>
            {(isActuallyExpanded || unmountOnExit) && (
                <div className={clsx([clsb([style, classNames], ['accordionBodyContainer']), !isActuallyExpanded && unmountOnExit ? 'hidden' : ''])}>
                    {bodyCells.map(cell => (
                        <AccordionCellNew
                            key={cell.id}
                            content={cell.content}
                            className={`${clsb([style, classNames], ['accordionBodyCell'])} ${cell.className || ''}`}
                        />
                    ))}
                </div>
            )}
        </AccessibilityElement>
    );
}, arePropsEqual);
